import {Carousel} from 'components/ui';
import {useQueryParam} from 'utils/hooks';
import {Activity} from 'APIs/letrusAPI/activities/types';
import styles from './stepper.module.scss';

enum ActivityTypeTranslations {
  'pedagogical content' = 'Leitura',
  'questionnaire' = 'Questionário',
  'write' = 'Escrita',
}

export type StepperBackgroundColors = 'white' | 'light-gray' | 'transparent';

export interface Step {
  label: string;
  status?: 'active' | 'inactive' | 'finished';
  id?: number;
  selected?: boolean;
  order?: number;
}

export interface StepperProps {
  learningPath?: Activity;
  carousel?: boolean;
  background?: StepperBackgroundColors;
  onStepClick?(stepId?: number): void;
}

function Stepper({
  learningPath,
  carousel,
  background = 'white',
  onStepClick,
}: StepperProps): JSX.Element {
  const queryParams = useQueryParam();
  const currentStepOrder = queryParams.get('passo');
  const ariaLabels = {
    active: 'Em andamento',
    inactive: 'Não iniciada',
    finished: 'Finalizada',
  };

  function ConditionalCarousel({useCarousel, children}) {
    return useCarousel ? (
      <Carousel
        userRole="student"
        minWidth={128}
        spaceBetweenSlides={0}
        slidesSwiping={false}
        enableKeyBoardsControls={false}
      >
        {children}
      </Carousel>
    ) : (
      children ?? <></>
    );
  }

  function getStepperData(learningPath?: Activity): Step[] {
    if (learningPath?.steps) {
      const isLearningPathFinished = learningPath.status === 'finished';

      return learningPath?.steps.map((step) => {
        let stepStatus: Step['status'] = 'inactive';

        if (step.order === learningPath.step_number) {
          stepStatus = 'active';
        }

        if (step.order < learningPath.step_number || isLearningPathFinished) {
          stepStatus = 'finished';
        }

        return {
          label: ActivityTypeTranslations[step.activity_type],
          status: stepStatus,
          id: step.id,
          selected: step.order === Number(currentStepOrder),
          order: step.order,
        };
      });
    }
    return [];
  }

  const getWriteSteps =
    learningPath?.steps?.filter((step) => step.activity_type === 'write') ?? [];

  return (
    <ul
      className={styles.wrapper}
      data-background={background}
      data-one-step={learningPath?.steps?.length === 1}
    >
      <ConditionalCarousel useCarousel={carousel}>
        {getStepperData(learningPath)?.map((step, index) => (
          <li key={step.id}>
            <button
              type="button"
              onClick={() => onStepClick?.(index + 1)}
              className={styles.step}
              data-one-step={learningPath?.steps?.length === 1}
            >
              <span
                className={styles.bulletBackground}
                aria-label={`${step.label}${ariaLabels[step.status || '']}`}
                data-background={background}
              >
                <span
                  className={styles.bullet}
                  data-state={step.status}
                  data-background={background}
                  data-selected={step.selected}
                  data-testid="bullet"
                >
                  {index + 1}
                </span>
              </span>
              <p className={styles.stepName} data-state={step.status}>
                {(step.label === 'write' ||
                  step.label.toLowerCase() === 'escrita') &&
                getWriteSteps?.length > 1 &&
                step?.order &&
                getWriteSteps[0].order < step.order
                  ? 'Reescrita'
                  : step.label}
              </p>
            </button>
          </li>
        ))}
      </ConditionalCarousel>
    </ul>
  );
}

export default Stepper;
